import { z } from 'zod';
import { loginRequestSchema } from './auth.js';
import { SSO_IDP_DISPLAY_NAME_MAX, enterpriseServiceProviderDTOSchema, ssoIdentityProviderDTOSchema, } from './sso-enterprise.js';
/** The providers a tenant can make mandatory. A subset of FEDERATED_PROVIDER_SLUGS (never 'apple'). */
/** `enterprise` = Okta / SAML / OIDC, never a FEDERATED shared button. */
export const SSO_PROVIDER_SLUGS = ['google', 'microsoft', 'enterprise'];
export const ssoProviderSlugSchema = z.enum(SSO_PROVIDER_SLUGS);
/** Mirrors Prisma `SsoConnectionStatus` (S50-kqp9-TASK-120). */
export const SSO_CONNECTION_STATUSES = ['CONFIGURED', 'ENFORCED'];
export const ssoConnectionStatusSchema = z.enum(SSO_CONNECTION_STATUSES);
/** Mirrors Prisma `SsoDomainVerificationMethod`. DNS_TXT for Google, ADMIN_CONSENT for Microsoft 365. */
export const SSO_DOMAIN_VERIFICATION_METHODS = [
    'DNS_TXT',
    'DIRECTORY_SYNC',
    'SUPPORT_EMAIL',
    'ADMIN_CONSENT',
];
export const ssoDomainVerificationMethodSchema = z.enum(SSO_DOMAIN_VERIFICATION_METHODS);
/** The TXT record the admin publishes: `_instacheckin-verification.<domain>` = `instacheckin-verification=<token>`. */
export const SSO_DNS_RECORD_LABEL = '_instacheckin-verification';
export const SSO_DNS_VALUE_PREFIX = 'instacheckin-verification=';
export function ssoDnsRecordName(domain) {
    return `${SSO_DNS_RECORD_LABEL}.${domain}`;
}
export function ssoDnsRecordValue(token) {
    return `${SSO_DNS_VALUE_PREFIX}${token}`;
}
/** "Turn on for everyone" needs the caller's own test sign-in within this many seconds (Technical Notes (h)). */
export const SSO_TEST_FRESH_SECONDS = 900;
/** Platform-admin Settings → Company sign-in. */
export const COMPANY_SIGN_IN_SETTINGS_PATH = '/settings/company-sign-in';
/** The central app's test sign-in page: `/company-sign-in/test?client=admin&provider=google`. */
export const COMPANY_SIGN_IN_TEST_PATH = '/company-sign-in/test';
/** The query parameter the central app returns to the settings page with: 'passed' or an ErrorCode. */
export const COMPANY_SIGN_IN_TEST_RESULT_PARAM = 'companyTest';
export const COMPANY_SIGN_IN_TEST_PASSED = 'passed';
/** The central app's approval page: `/company-sign-in/consent?client=admin` starts; Microsoft redirects back to it. */
export const COMPANY_SIGN_IN_CONSENT_PATH = '/company-sign-in/consent';
/** The query parameter the central app returns to Settings with: 'granted' or an ErrorCode. */
export const COMPANY_SIGN_IN_CONSENT_RESULT_PARAM = 'companyConsent';
export const COMPANY_SIGN_IN_CONSENT_GRANTED = 'granted';
/** An Entra tenant id (`tid`): a GUID, lower-cased. */
export const entraTenantIdSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/);
/** The sealed consent state: base64url, the federated state's bounds. */
export const companyConsentStateSchema = z.string().regex(/^[A-Za-z0-9_-]{40,2048}$/);
/** `POST /api/web/tenant/sso/microsoft/consent/start`. */
export const microsoftConsentStartDTOSchema = z.object({ consentUrl: z.string().url() }).strict();
/** `POST /api/web/tenant/sso/microsoft/consent/complete` — what Microsoft put on the redirect, relayed by the central page. */
export const completeMicrosoftConsentSchema = z.union([
    z.object({ state: companyConsentStateSchema, tenant: entraTenantIdSchema }).strict(),
    z.object({ state: companyConsentStateSchema, error: z.string().min(1).max(200) }).strict(),
]);
/** A DNS name: lower-cased, 3–253 chars, labels of 1–63 [a-z0-9-] not starting/ending with '-', a 2–63 letter TLD. */
export const ssoDomainNameSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(253)
    .regex(/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/);
export const ssoDomainDTOSchema = z
    .object({
    /** A DNS name (Google) or a lower-case Entra tenant id (Microsoft 365). */
    domain: z.string(),
    verifiedAt: z.string().datetime().nullable(),
    verificationMethod: ssoDomainVerificationMethodSchema.nullable(),
    /** What to publish (Google). Null for a Microsoft 365 claim, which is proven by admin consent. */
    dnsRecord: z.object({ name: z.string(), value: z.string() }).strict().nullable(),
    /** Microsoft 365 only: the organization's name from Microsoft Graph. */
    organizationName: z.string().min(1).optional(),
    /** Microsoft 365 only: its verified domains, lower-case, the default first. */
    organizationDomains: z.array(z.string()).max(50).optional(),
    /** Support's verify-by-email window in force (ISO), else null. Absent for a Microsoft 365 claim and from an older API. */
    emailFallbackUntil: z.string().datetime().nullable().optional(),
})
    .strict();
export const companySignInConnectionDTOSchema = z
    .object({
    provider: ssoProviderSlugSchema,
    status: ssoConnectionStatusSchema,
    domains: z.array(ssoDomainDTOSchema).max(20),
    /** The latest successful test sign-in; `byCaller` and `fresh` are computed for the requesting user. */
    lastTest: z
        .object({ at: z.string().datetime(), byCaller: z.boolean(), fresh: z.boolean() })
        .strict()
        .nullable(),
    enforcedAt: z.string().datetime().nullable(),
    /** A support pause in force: when it ends (passwords work until then). Null when there is none (ADR 0042 decision 14). */
    enforcementSuspendedUntil: z.string().datetime().nullable(),
    /** ACTIVE members whose login email is outside every claimed domain (the confirmation states it). */
    membersOutsideClaim: z.number().int().nonnegative(),
    /** entitled && CONFIGURED && a verified domain && lastTest.fresh. */
    canEnforce: z.boolean(),
    /** Enterprise connections only: the configured IdP, or null before it is set up. Absent for Google and Microsoft. */
    identityProvider: ssoIdentityProviderDTOSchema.nullable().optional(),
    /** Enterprise only: create a login on first sign-in for a matching active person (ADR 0042 decision 47). */
    jitProvisioning: z.boolean().optional(),
})
    .strict();
/** `GET /api/web/tenant/sso`. `entitled` false + `connection` null = the upgrade state. */
export const companySignInDTOSchema = z
    .object({
    entitled: z.boolean(),
    /** False when this deployment has no domain verifier (`SSO_DOMAIN_VERIFIER` resolves to disabled). */
    dnsCheckAvailable: z.boolean(),
    /** True when this deployment can claim a Microsoft 365 organization (ADR 0042 decision 25). Absent = false. */
    microsoftAvailable: z.boolean().optional(),
    connection: companySignInConnectionDTOSchema.nullable(),
    /** The values to paste into an Okta / SAML / OIDC app; null when this deployment cannot host one. Absent = an older API. */
    enterpriseServiceProvider: enterpriseServiceProviderDTOSchema.nullable().optional(),
})
    .strict();
/** `POST /api/web/tenant/sso/domains/verify`. */
export const verifySsoDomainSchema = z.object({ domain: ssoDomainNameSchema }).strict();
export const SSO_ENTERPRISE_MAX_DOMAINS = 20;
/** `POST /api/web/tenant/sso/enterprise/domains` — claim one more domain for Okta / SAML / OIDC. */
export const addEnterpriseDomainSchema = z.object({ domain: ssoDomainNameSchema }).strict();
/** `POST /api/web/tenant/sso/enforcement` — the admin types the domain to confirm. */
export const enableSsoEnforcementSchema = z
    .object({ confirmDomain: z.string().trim().toLowerCase().min(1).max(253) })
    .strict();
/** `POST /api/web/auth/sso/discover` — the email typed on the central sign-in page. */
export const signInDiscoveryRequestSchema = z
    .object({ email: loginRequestSchema.shape.email })
    .strict();
export const signInDiscoveryDTOSchema = z.discriminatedUnion('method', [
    z.object({ method: z.literal('password') }).strict(),
    z
        .object({
        method: z.literal('company'),
        provider: ssoProviderSlugSchema,
        /** Enterprise only: the IdP's display name ("Continue with <label>"). */
        label: z.string().min(1).max(SSO_IDP_DISPLAY_NAME_MAX).optional(),
    })
        .strict(),
    /** A classic-portal customer with no account here: send the browser to `url` (docs/025). */
    z.object({ method: z.literal('legacy'), url: z.string().url() }).strict(),
]);
export function ssoRequiredCodeFor(provider) {
    switch (provider) {
        case 'google':
            return 'SSO_GOOGLE_REQUIRED';
        case 'microsoft':
            return 'SSO_MICROSOFT_REQUIRED';
        case 'enterprise':
            return 'SSO_ENTERPRISE_REQUIRED';
    }
}
/** Federated sessions in a tenant whose company sign-in is in force are re-checked at the provider this often (owner decision (a)4). */
export const SSO_FEDERATED_REAUTH_SECONDS = 3600;
/** Support may pause enforcement for 1–72 whole hours (open question (q)4). */
export const SSO_SUSPENSION_MIN_HOURS = 1;
export const SSO_SUSPENSION_MAX_HOURS = 72;
export const SSO_SUPPORT_REASON_MIN_LENGTH = 10;
export const SSO_SUPPORT_REASON_MAX_LENGTH = 500;
const ssoSupportReasonSchema = z
    .string()
    .trim()
    .min(SSO_SUPPORT_REASON_MIN_LENGTH)
    .max(SSO_SUPPORT_REASON_MAX_LENGTH);
/** `POST /api/web/sso/tenants/:tenantId/enforcement-suspension` — operator only. */
export const suspendSsoEnforcementSchema = z
    .object({
    hours: z.number().int().min(SSO_SUSPENSION_MIN_HOURS).max(SSO_SUSPENSION_MAX_HOURS),
    reason: ssoSupportReasonSchema,
})
    .strict();
/** `POST /api/web/sso/tenants/:tenantId/enforcement-suspension/end` — operator only. */
export const resumeSsoEnforcementSchema = z.object({ reason: ssoSupportReasonSchema }).strict();
/** The `details` of `401 SSO_REAUTH_REQUIRED` on `POST /api/web/auth/session`: whom to bounce, and where. */
export const ssoReauthRequiredDetailsSchema = z
    .object({ provider: ssoProviderSlugSchema, email: loginRequestSchema.shape.email })
    .strict();
/** `GET /api/web/sso/tenants/:tenantId` — a tenant's company sign-in as an operator sees it. */
export const tenantCompanySignInAdminDTOSchema = z
    .object({
    tenantId: z.string(),
    tenantName: z.string(),
    connection: z
        .object({
        provider: ssoProviderSlugSchema,
        status: ssoConnectionStatusSchema,
        domains: z
            .array(z
            .object({
            domain: z.string(),
            verifiedAt: z.string().datetime().nullable(),
            emailFallbackUntil: z.string().datetime().nullable().optional(),
        })
            .strict())
            .max(20),
        enforcedAt: z.string().datetime().nullable(),
        /** The pause in force, or null (an expired pause reads null). */
        suspension: z
            .object({ until: z.string().datetime(), suspendedAt: z.string().datetime() })
            .strict()
            .nullable(),
    })
        .strict()
        .nullable(),
})
    .strict();
/** Owner decision (a)3: the only mailboxes a domain code may go to. */
export const SSO_EMAIL_FALLBACK_MAILBOXES = ['admin', 'postmaster', 'hostmaster'];
export const ssoEmailFallbackMailboxSchema = z.enum(SSO_EMAIL_FALLBACK_MAILBOXES);
/** Support enables verify-by-email for 1–7 whole days (open question (q)38). */
export const SSO_EMAIL_FALLBACK_MIN_DAYS = 1;
export const SSO_EMAIL_FALLBACK_MAX_DAYS = 7;
/** A domain email code is valid this long (owner decision (a)3). */
export const SSO_DOMAIN_EMAIL_CODE_TTL_HOURS = 24;
/** `POST /api/web/sso/tenants/:tenantId/email-fallback` — operator only. */
export const enableSsoEmailFallbackSchema = z
    .object({
    domain: ssoDomainNameSchema,
    days: z.number().int().min(SSO_EMAIL_FALLBACK_MIN_DAYS).max(SSO_EMAIL_FALLBACK_MAX_DAYS),
    reason: ssoSupportReasonSchema,
})
    .strict();
/** `POST /api/web/sso/tenants/:tenantId/email-fallback/end` — operator only. */
export const endSsoEmailFallbackSchema = z
    .object({ domain: ssoDomainNameSchema, reason: ssoSupportReasonSchema })
    .strict();
/** `POST /api/web/tenant/sso/domains/email-code` — send a code to <mailbox>@<domain>. */
export const sendSsoDomainEmailCodeSchema = z
    .object({ domain: ssoDomainNameSchema, mailbox: ssoEmailFallbackMailboxSchema })
    .strict();
/** `POST /api/web/tenant/sso/domains/email-code/verify`. */
export const verifySsoDomainEmailCodeSchema = z
    .object({
    domain: ssoDomainNameSchema,
    mailbox: ssoEmailFallbackMailboxSchema,
    code: z.string().regex(/^\d{6}$/),
})
    .strict();
/** `PUT /api/web/tenant/sso/jit-provisioning` — enterprise connections only. */
export const setSsoJitProvisioningSchema = z.object({ enabled: z.boolean() }).strict();
//# sourceMappingURL=sso.js.map